import React, { useCallback, useMemo, useState } from 'react';
import { ApiReferenceEnums } from 'enums/ApiReferenceEnums';
import styles from './ApiReference.module.scss';
import {
  EXAMPLE_API_KEY,
  PRODUCTION_API_BASE,
  globalErrorExamples,
  overviewBlocks,
  quickReferenceRows,
  resourceCategories,
  type ErrorRow,
  type FieldRow,
  type GlobalErrorExample,
  type HeaderRow,
  type QueryParamRow,
  type ReferenceCategory,
  type ReferenceEndpointBlock,
  type ReferenceOverviewBlock,
  type ResponseBlock,
  type RichListSegment,
} from './carbonApiReferenceData';

const overviewAnchor = (id: string): string => `overview-${id}`;
const endpointAnchor = (categoryId: string, endpointId: string): string =>
  `endpoint-${categoryId}--${endpointId}`;

const buildFullUrl = (path: string): string => {
  const base = PRODUCTION_API_BASE.replace(/\/$/, '');
  if (!path) {
    return base;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
};

const methodClassName = (method: string): string => {
  const upper = method.toUpperCase();
  if (upper === 'GET') return `${styles.method} ${styles.methodGet}`;
  if (upper === 'POST') return `${styles.method} ${styles.methodPost}`;
  if (upper === 'PUT') return `${styles.method} ${styles.methodPut}`;
  if (upper === 'DELETE') return `${styles.method} ${styles.methodDelete}`;
  if (upper === 'PATCH') return `${styles.method} ${styles.methodPatch}`;
  return `${styles.method} ${styles.methodDefault}`;
};

const statusClassName = (status: string): string => {
  const code = parseInt(status, 10);
  if (code >= 500) return `${styles.statusBadge} ${styles.statusError}`;
  if (code === 429) return `${styles.statusBadge} ${styles.statusRate}`;
  if (code >= 400) return `${styles.statusBadge} ${styles.statusClient}`;
  if (code >= 300) return `${styles.statusBadge} ${styles.statusRedirect}`;
  return `${styles.statusBadge} ${styles.statusOk}`;
};

type JsonTokenProps = { content: string };
const JsonHighlighted: React.FC<JsonTokenProps> = ({ content }) => {
  const tokens = useMemo(() => {
    const pattern =
      /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
    const nodes: Array<{ type: string; text: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
      if (match.index > lastIndex) {
        nodes.push({ type: 'plain', text: content.slice(lastIndex, match.index) });
      }
      const [, stringMatch, colon, literalMatch, numberMatch] = match;
      if (stringMatch) {
        nodes.push({ type: colon ? 'key' : 'string', text: stringMatch });
        if (colon) nodes.push({ type: 'plain', text: colon });
      } else if (literalMatch) {
        nodes.push({ type: literalMatch === 'null' ? 'null' : 'bool', text: literalMatch });
      } else if (numberMatch) {
        nodes.push({ type: 'number', text: numberMatch });
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      nodes.push({ type: 'plain', text: content.slice(lastIndex) });
    }
    return nodes;
  }, [content]);

  return (
    <>
      {tokens.map((t, i) => {
        if (t.type === 'plain') return <React.Fragment key={i}>{t.text}</React.Fragment>;
        const className =
          t.type === 'key'
            ? styles.jsonKey
            : t.type === 'string'
              ? styles.jsonString
              : t.type === 'number'
                ? styles.jsonNumber
                : t.type === 'bool'
                  ? styles.jsonBool
                  : styles.jsonNull;
        return (
          <span key={i} className={className}>
            {t.text}
          </span>
        );
      })}
    </>
  );
};

type CopyButtonProps = { value: string; ariaLabel?: string };
const CopyButton: React.FC<CopyButtonProps> = ({ value, ariaLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    const reset = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(reset).catch(reset);
      return;
    }
    reset();
  }, [value]);

  return (
    <button
      type="button"
      className={styles.copyButton}
      onClick={handleCopy}
      aria-label={ariaLabel ?? ApiReferenceEnums.COPY_BUTTON}>
      {copied ? ApiReferenceEnums.COPIED_BUTTON : ApiReferenceEnums.COPY_BUTTON}
    </button>
  );
};

type CodePanelProps = {
  label: string;
  language: 'json' | 'bash' | 'plain';
  code: string;
  copyable?: boolean;
};
const CodePanel: React.FC<CodePanelProps> = ({ label, language, code, copyable = true }) => (
  <div className={styles.codePanel}>
    <div className={styles.codePanelHeader}>
      <span className={styles.codePanelLabel}>{label}</span>
      {copyable ? <CopyButton value={code} ariaLabel={`Copy ${label}`} /> : null}
    </div>
    <pre className={`${styles.codeBlock} ${styles.codeBlockDark}`}>
      <code className={styles[`lang_${language}`] ?? ''}>
        {language === 'json' ? <JsonHighlighted content={code} /> : code}
      </code>
    </pre>
  </div>
);

type SectionHeadingProps = { id?: string; children: React.ReactNode; level?: 2 | 3 | 4 };
const SectionHeading: React.FC<SectionHeadingProps> = ({ id, children, level = 3 }) => {
  if (level === 2) {
    return (
      <h2 className={styles.h2} id={id}>
        {children}
      </h2>
    );
  }
  if (level === 4) {
    return (
      <h4 className={styles.h4} id={id}>
        {children}
      </h4>
    );
  }
  return (
    <h3 className={styles.h3} id={id}>
      {children}
    </h3>
  );
};

const HttpsBanner: React.FC = () => (
  <div className={styles.httpsBanner} role="note">
    <span className={styles.httpsBannerIcon} aria-hidden>
      !
    </span>
    <div>
      <div className={styles.httpsBannerTitle}>{ApiReferenceEnums.BANNER_HTTPS}</div>
      <div className={styles.httpsBannerText}>{ApiReferenceEnums.BANNER_HTTPS_DESC}</div>
    </div>
  </div>
);

const renderRichListSegment = (seg: RichListSegment, key: number): React.ReactNode => {
  if (seg.kind === 'bold') {
    return (
      <strong key={key} className={styles.apiKeyGuideStrong}>
        {seg.text}
      </strong>
    );
  }
  if (seg.kind === 'italic') {
    return (
      <em key={key} className={styles.apiKeyGuideEm}>
        {seg.text}
      </em>
    );
  }
  return <span key={key}>{seg.text}</span>;
};

type OverviewSectionProps = { block: ReferenceOverviewBlock };
const OverviewSection: React.FC<OverviewSectionProps> = ({ block }) => {
  const headingId = overviewAnchor(block.id);
  const useRichSteps = Boolean(block.richOrderedSteps?.length);

  return (
    <section className={styles.docSection} aria-labelledby={headingId}>
      <SectionHeading level={2} id={headingId}>
        {block.title}
      </SectionHeading>
      {block.paragraphs.map(p => (
        <p key={p} className={styles.p}>
          {p}
        </p>
      ))}
      {block.listItems?.length ? (
        <ul className={styles.bulletList}>
          {block.listItems.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {useRichSteps ? (
        <ol className={`${styles.stepsList} ${styles.apiKeyGuideStepsList}`}>
          {block.richOrderedSteps!.map((step, i) => (
            <li key={`${block.id}-rich-${i}`} className={styles.apiKeyGuideStepItem}>
              <div className={styles.apiKeyGuideStepHeading}>{step.heading}</div>
              <p className={styles.apiKeyGuideStepDescription}>
                {step.segments.map((seg, j) => renderRichListSegment(seg, j))}
              </p>
            </li>
          ))}
        </ol>
      ) : block.orderedListItems?.length ? (
        <ol className={styles.stepsList}>
          {block.orderedListItems.map((item, i) => (
            <li key={`${block.id}-step-${i}`}>{item}</li>
          ))}
        </ol>
      ) : null}
      {block.codeBlocks?.map(code => (
        <pre key={code} className={styles.codeBlock}>
          {code}
        </pre>
      ))}
    </section>
  );
};

type QueryParamsTableProps = { params: QueryParamRow[]; emptyNote?: string };
const QueryParamsTable: React.FC<QueryParamsTableProps> = ({ params, emptyNote }) => {
  if (!params.length) {
    return (
      <p className={`${styles.p} ${styles.mutedNote}`}>
        {emptyNote ?? ApiReferenceEnums.NO_QUERY_PARAMS}
      </p>
    );
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.refTable}>
        <thead>
          <tr>
            <th>{ApiReferenceEnums.TABLE_PARAM}</th>
            <th>{ApiReferenceEnums.TABLE_TYPE}</th>
            <th>{ApiReferenceEnums.TABLE_REQUIRED}</th>
            <th>{ApiReferenceEnums.TABLE_DESCRIPTION}</th>
          </tr>
        </thead>
        <tbody>
          {params.map(row => (
            <tr key={row.name}>
              <td>
                <code className={styles.inlineCode}>{row.name}</code>
              </td>
              <td>{row.type}</td>
              <td>
                {row.required ? (
                  <span className={`${styles.tag} ${styles.tagRequired}`}>
                    {ApiReferenceEnums.REQUIRED_BADGE}
                  </span>
                ) : (
                  <span className={`${styles.tag} ${styles.tagOptional}`}>
                    {ApiReferenceEnums.OPTIONAL_BADGE}
                  </span>
                )}
              </td>
              <td>{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type HeadersTableProps = { headers: HeaderRow[] };
const HeadersTable: React.FC<HeadersTableProps> = ({ headers }) => (
  <div className={styles.tableWrap}>
    <table className={styles.refTable}>
      <thead>
        <tr>
          <th>{ApiReferenceEnums.TABLE_HEADER}</th>
          <th>{ApiReferenceEnums.TABLE_REQUIRED}</th>
          <th>{ApiReferenceEnums.TABLE_VALUE}</th>
        </tr>
      </thead>
      <tbody>
        {headers.map(h => (
          <tr key={h.name}>
            <td>
              <code className={styles.inlineCode}>{h.name}</code>
            </td>
            <td>
              {h.required ? (
                <span className={`${styles.tag} ${styles.tagRequired}`}>
                  {ApiReferenceEnums.YES}
                </span>
              ) : (
                <span className={`${styles.tag} ${styles.tagOptional}`}>
                  {ApiReferenceEnums.NO}
                </span>
              )}
            </td>
            <td>
              <code className={styles.inlineCode}>{h.description}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

type FieldsTableProps = { fields: FieldRow[] };
const FieldsTable: React.FC<FieldsTableProps> = ({ fields }) => (
  <div className={styles.tableWrap}>
    <table className={styles.refTable}>
      <thead>
        <tr>
          <th>{ApiReferenceEnums.TABLE_FIELD}</th>
          <th>{ApiReferenceEnums.TABLE_DESCRIPTION}</th>
        </tr>
      </thead>
      <tbody>
        {fields.map(f => (
          <tr key={f.field}>
            <td>
              <code className={styles.inlineCode}>{f.field}</code>
            </td>
            <td>{f.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

type ErrorsTableProps = { errors: ErrorRow[] };
const ErrorsTable: React.FC<ErrorsTableProps> = ({ errors }) => (
  <div className={styles.tableWrap}>
    <table className={styles.refTable}>
      <thead>
        <tr>
          <th>{ApiReferenceEnums.TABLE_STATUS}</th>
          <th>{ApiReferenceEnums.TABLE_SITUATION}</th>
          <th>{ApiReferenceEnums.TABLE_MESSAGE}</th>
        </tr>
      </thead>
      <tbody>
        {errors.map((e, i) => (
          <tr key={`${e.status}-${e.situation}-${i}`}>
            <td>
              <span className={statusClassName(e.status)}>{e.status}</span>
            </td>
            <td>{e.situation}</td>
            <td>{e.message}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

type MethodUrlRowProps = { method: string; path: string };
const MethodUrlRow: React.FC<MethodUrlRowProps> = ({ method, path }) => (
  <div className={styles.endpointRow}>
    <span className={methodClassName(method)}>{method}</span>
    <code className={styles.path}>{buildFullUrl(path)}</code>
  </div>
);

type ResponseBlocksProps = { responses: ResponseBlock[] };
const ResponseBlocks: React.FC<ResponseBlocksProps> = ({ responses }) => (
  <>
    {responses.map(r => (
      <CodePanel key={r.label} label={r.label} language="json" code={r.content} />
    ))}
  </>
);

type EndpointDocProps = {
  categoryId: string;
  endpoint: ReferenceEndpointBlock;
  apiKey: string;
};
const EndpointDoc: React.FC<EndpointDocProps> = ({ categoryId, endpoint, apiKey }) => {
  const anchor = endpointAnchor(categoryId, endpoint.id);
  const personalizedCurl = endpoint.curl.replace(EXAMPLE_API_KEY, apiKey || EXAMPLE_API_KEY);

  return (
    <div className={styles.endpointCard} id={anchor}>
      <section className={styles.docSection} aria-labelledby={`${anchor}-title`}>
        <SectionHeading id={`${anchor}-title`}>{endpoint.title}</SectionHeading>
        <div className={styles.endpointHeaderRow}>
          <MethodUrlRow method={endpoint.method} path={endpoint.path} />
          <span className={`${styles.tag} ${styles.tagAuth}`}>
            {ApiReferenceEnums.AUTH_REQUIRED_BADGE}
          </span>
        </div>

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_DESCRIPTION}</SectionHeading>
        <p className={styles.p}>{endpoint.description}</p>

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_REQUEST_PARAMS}</SectionHeading>
        <QueryParamsTable params={endpoint.queryParams} emptyNote={endpoint.queryParamsNote} />

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_HEADERS}</SectionHeading>
        <HeadersTable headers={endpoint.headers} />

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_REQUEST_EXAMPLE}</SectionHeading>
        <CodePanel label="curl" language="bash" code={personalizedCurl} />

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_RESPONSE_EXAMPLE}</SectionHeading>
        <ResponseBlocks responses={endpoint.responses} />

        <details className={styles.collapsible}>
          <summary className={styles.collapsibleSummary}>
            {ApiReferenceEnums.SECTION_FIELDS}
          </summary>
          <div className={styles.collapsibleBody}>
            <FieldsTable fields={endpoint.fields} />
          </div>
        </details>

        {endpoint.businessNotes.length ? (
          <div className={styles.infoBox}>
            <div className={styles.infoBoxTitle}>
              {ApiReferenceEnums.SECTION_BUSINESS_NOTES}
            </div>
            <ul className={styles.bulletList}>
              {endpoint.businessNotes.map(note => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <SectionHeading level={4}>{ApiReferenceEnums.SECTION_ERRORS}</SectionHeading>
        <ErrorsTable errors={endpoint.errors} />
      </section>
    </div>
  );
};

type CategorySectionProps = { category: ReferenceCategory; apiKey: string };
const CategorySection: React.FC<CategorySectionProps> = ({ category, apiKey }) => (
  <section
    className={styles.categoryWrap}
    id={`category-${category.id}`}
    aria-label={category.navLabel}>
    {category.sectionIntro?.map(p => (
      <p key={p} className={styles.p}>
        {p}
      </p>
    ))}
    {category.endpoints.map(ep => (
      <EndpointDoc key={ep.id} categoryId={category.id} endpoint={ep} apiKey={apiKey} />
    ))}
  </section>
);

type TryItPanelProps = { apiKey: string; onApiKeyChange: (value: string) => void };
const TryItPanel: React.FC<TryItPanelProps> = ({ apiKey, onApiKeyChange }) => {
  const exampleEndpoint = resourceCategories[0]?.endpoints.find(e => e.id === 'ocf-organization-summary');
  const effectiveKey = apiKey || EXAMPLE_API_KEY;
  const curl =
    exampleEndpoint?.curl.replace(EXAMPLE_API_KEY, effectiveKey) ??
    [
      `curl -sS -G "${PRODUCTION_API_BASE}/v1/ocf/organization-summary" \\`,
      `  --data-urlencode "organization_id=102" \\`,
      `  --data-urlencode "year=2024" \\`,
      `  -H "Authorization: Bearer ${effectiveKey}"`,
    ].join('\n');

  return (
    <section className={styles.tryItPanel} id="try-it" aria-label="Try it">
      <SectionHeading level={2} id="try-it-heading">
        {ApiReferenceEnums.TRY_IT_TITLE}
      </SectionHeading>
      <p className={styles.p}>{ApiReferenceEnums.TRY_IT_DESC}</p>
      <label className={styles.inputLabel} htmlFor="api-reference-try-it-key">
        {ApiReferenceEnums.TRY_IT_LABEL}
      </label>
      <input
        id="api-reference-try-it-key"
        type="text"
        className={styles.textInput}
        placeholder={ApiReferenceEnums.TRY_IT_PLACEHOLDER}
        value={apiKey}
        onChange={e => onApiKeyChange(e.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
      <CodePanel label="curl (Organization summary)" language="bash" code={curl} />
    </section>
  );
};

const GlobalErrorsSection: React.FC = () => (
  <section className={styles.docSection} id="error-handling" aria-labelledby="error-handling-heading">
    <SectionHeading level={2} id="error-handling-heading">
      {ApiReferenceEnums.ERROR_HANDLING_TITLE}
    </SectionHeading>
    <p className={styles.p}>{ApiReferenceEnums.ERROR_HANDLING_INTRO}</p>
    <div className={styles.alertGrid}>
      {globalErrorExamples.map((e: GlobalErrorExample) => (
        <div
          key={e.id}
          className={`${styles.alertBox} ${styles[`alert_${e.status}`] ?? ''}`}
          role="alert">
          <div className={styles.alertHeader}>
            <span className={statusClassName(e.status)}>{e.status}</span>
            <span className={styles.alertTitle}>{e.title}</span>
          </div>
          <p className={styles.alertDesc}>{e.description}</p>
          <CodePanel label={`${e.status} response`} language="json" code={e.json} />
        </div>
      ))}
    </div>
  </section>
);

const QuickReferenceSection: React.FC = () => (
  <section className={styles.docSection} id="quick-reference" aria-labelledby="quick-reference-heading">
    <SectionHeading level={2} id="quick-reference-heading">
      {ApiReferenceEnums.QUICK_REF_TITLE}
    </SectionHeading>
    <p className={styles.p}>{ApiReferenceEnums.QUICK_REF_INTRO}</p>
    <div className={styles.tableWrap}>
      <table className={styles.refTable}>
        <thead>
          <tr>
            <th>{ApiReferenceEnums.TABLE_ENDPOINT}</th>
            <th>{ApiReferenceEnums.TABLE_ALLOWED}</th>
          </tr>
        </thead>
        <tbody>
          {quickReferenceRows.map(row => (
            <tr key={row.endpoint}>
              <td>
                <code className={styles.inlineCode}>{row.endpoint}</code>
              </td>
              <td>
                <code className={styles.inlineCode}>{row.allowed}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const ApiReference: React.FC = () => {
  const [apiKey, setApiKey] = useState('');

  const lastUpdated = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  const tocEntries: { href: string; label: string }[] = [
    { href: '#api-reference-top', label: ApiReferenceEnums.H1_REFERENCE },
    ...overviewBlocks.map(b => ({
      href: `#${overviewAnchor(b.id)}`,
      label: b.navLabel,
    })),
    ...resourceCategories.flatMap(c => [
      { href: `#category-${c.id}`, label: c.navLabel },
      ...c.endpoints.map(ep => ({
        href: `#${endpointAnchor(c.id, ep.id)}`,
        label: ep.navLabel,
      })),
    ]),
    { href: '#error-handling', label: ApiReferenceEnums.ERROR_HANDLING_TITLE },
    { href: '#quick-reference', label: ApiReferenceEnums.QUICK_REF_TITLE },
    { href: '#try-it', label: ApiReferenceEnums.TRY_IT_TITLE },
  ];

  return (
    <div className={styles.layout}>
      <a href="#main-content" className={styles.skipLink}>
        {ApiReferenceEnums.SKIP_LINK}
      </a>
      <header className={styles.topBar}>
        <h1 className={styles.topBarTitle}>{ApiReferenceEnums.PAGE_TITLE}</h1>
        <span className={styles.topBarMeta}>{ApiReferenceEnums.PAGE_SUBTITLE}</span>
      </header>
      <div className={styles.body}>
        <nav className={styles.sidebar} aria-label="API reference sections">
          <div className={styles.navGroupLabel}>{ApiReferenceEnums.SIDEBAR_OVERVIEW}</div>
          <a className={styles.navLink} href="#api-reference-top">
            {ApiReferenceEnums.H1_REFERENCE}
          </a>
          {overviewBlocks.map(b => (
            <a key={b.id} className={styles.navLink} href={`#${overviewAnchor(b.id)}`}>
              {b.navLabel}
            </a>
          ))}
          <div className={styles.navGroupLabel}>{ApiReferenceEnums.SIDEBAR_ENDPOINTS}</div>
          {resourceCategories.map(c => (
            <React.Fragment key={c.id}>
              <a className={styles.navLink} href={`#category-${c.id}`}>
                {c.navLabel}
              </a>
              {c.endpoints.map(ep => (
                <a
                  key={ep.id}
                  className={`${styles.navLink} ${styles.navLinkSub}`}
                  href={`#${endpointAnchor(c.id, ep.id)}`}>
                  {ep.navLabel}
                </a>
              ))}
            </React.Fragment>
          ))}
          <div className={styles.navGroupLabel}>{ApiReferenceEnums.SIDEBAR_REFERENCE}</div>
          <a className={styles.navLink} href="#error-handling">
            {ApiReferenceEnums.ERROR_HANDLING_TITLE}
          </a>
          <a className={styles.navLink} href="#quick-reference">
            {ApiReferenceEnums.QUICK_REF_TITLE}
          </a>
          <a className={styles.navLink} href="#try-it">
            {ApiReferenceEnums.TRY_IT_TITLE}
          </a>
        </nav>
        <div className={styles.main}>
          <article id="main-content" className={styles.article}>
            <p className={styles.topBarMeta}>{ApiReferenceEnums.BREADCRUMB_CURRENT}</p>
            <header className={styles.hero} id="api-reference-top">
              <h1 className={styles.h1}>{ApiReferenceEnums.H1_REFERENCE}</h1>
              <p className={styles.lead}>{ApiReferenceEnums.HERO_LEAD}</p>
            </header>
            <HttpsBanner />
            {overviewBlocks.map(b => (
              <OverviewSection key={b.id} block={b} />
            ))}
            <SectionHeading level={2} id="endpoints-root">
              {ApiReferenceEnums.H2_ENDPOINTS}
            </SectionHeading>
            <p className={styles.p}>{ApiReferenceEnums.ENDPOINTS_INTRO}</p>
            {resourceCategories.map(c => (
              <CategorySection key={c.id} category={c} apiKey={apiKey} />
            ))}
            <GlobalErrorsSection />
            <QuickReferenceSection />
            <TryItPanel apiKey={apiKey} onApiKeyChange={setApiKey} />
            <footer className={styles.footerNote}>
              {ApiReferenceEnums.LAST_UPDATED_PREFIX} {lastUpdated}
            </footer>
          </article>
          <nav className={styles.toc} aria-label="On this page">
            <p className={styles.tocTitle}>{ApiReferenceEnums.ON_THIS_PAGE}</p>
            <div className={styles.tocScroll}>
              {tocEntries.map((entry, i) => (
                <a
                  key={`${entry.href}-${entry.label}-${i}`}
                  className={styles.tocLink}
                  href={entry.href}>
                  {entry.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default ApiReference;
